import { Divider, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ColDef, ICellRendererParams, IRichCellEditorParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    activityStateEnum,
    activityTypeEnum,
    getActivityStateLabel,
    getTruckGroupLabel,
    getTruckModeLabel,
    localStorageKeys,
    permissionLevelEnum,
    reportStateEnum,
    reportTypeEnum,
    truckGroupEnum,
} from '../../../../constants';
import { getActivityStateColor } from '../../../../constants/colors';
import {
    useActivityTruckManagerMutation,
    useDeleteActivityMutation,
    useUpdateActivityMutation,
} from '../../../../hooks/mutations/useActivityMutation';
import {
    useGetBackPlanningForActivityMutation,
    useGetBackPlanningMutation,
    useSendPlanningForActivityMutation,
    useSendPlanningMutation,
} from '../../../../hooks/mutations/usePlanningMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { useBoolean } from '../../../../hooks/useBoolean';
import { useTheme } from '../../../../hooks/useTheme';
import { DataItem } from '../../../../services/apiSLTP/dailyReports/activities';
import { ActivityType } from '../../../../services/apiSLTP/dailyReports/activityTypes';
import { createReportIfEmpty } from '../../../../services/apiSLTP/dailyReports/reports';
import { Truck } from '../../../../services/apiSLTP/materials/trucks';
import { User } from '../../../../services/apiSLTP/users';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { formatDate, formatTime } from '../../../../utils/dates';
import MachinesCell from '../../../cells/MachinesCell';
import RentalsCell from '../../../cells/RentalsCell';
import SiteCell from '../../../cells/SiteCell';
import StartTimeCell from '../../../cells/StartTimeCell';
import StateCell from '../../../cells/StateCell';
import SubContractorsCell from '../../../cells/SubContractorsCell';
import TempWorkersCell from '../../../cells/TempWorkersCell';
import ToolsCell from '../../../cells/ToolsCell';
import TrucksPlannedCell from '../../../cells/TrucksPlannedCell';
import TruckIcon from '../../../domain/TruckIcon';
import CreateActivityModal from '../../../modals/operations/planning/CreateActivityModal';
import DuplicateActivitiesModal from '../../../modals/operations/planning/DuplicateActivitiesModal';
import DuplicateActivityModal from '../../../modals/operations/planning/DuplicateActivityModal';
import RemainingMaterialModal from '../../../modals/operations/planning/RemainingMaterialModal';
import RemainingPeopleModal from '../../../modals/operations/planning/RemainingPeopleModal';
import CustomToggle from '../../../ui/CustomToggle';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface PlanningGlobalTableProps {
    currentDate: Date;
    activities: DataItem[];
    refetch: () => void;
    isLoading: boolean;
    dataTrucks: Truck[];
    dataActivitiesTypes: ActivityType[];
    dataUsers: User[];
    checkEditableRow: (activity: DataItem) => boolean;
    isUsersLoading: boolean;
    isActivitiesTypesLoading: boolean;
    handleAlignment?: (event: React.MouseEvent<HTMLElement>, newAlignment: number | null) => void;
}

const PlanningGlobalTable: React.FC<PlanningGlobalTableProps> = ({
    currentDate,
    activities,
    isLoading,
    refetch,
    dataActivitiesTypes,
    dataTrucks,
    dataUsers,
    checkEditableRow,
    isActivitiesTypesLoading,
    isUsersLoading,
    handleAlignment,
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const { muiTheme } = useTheme();

    const duplicateActivityModal = useBoolean(false);
    const createActivityModal = useBoolean(false);

    const [selectedActivityForDuplicate, setSelectedActivityForDuplicate] = useState<DataItem | null>(null);

    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [showDuplicateActivities, setShowDuplicateActivities] = useState(false);

    const remainingUsersModal = useBoolean(false);
    const remainingMaterialModal = useBoolean(false);

    const { mutate: updateActivity } = useUpdateActivityMutation({ onSuccess: () => refetch() });
    const { mutate: deleteActivity } = useDeleteActivityMutation({ onSuccess: () => refetch() });

    const { mutate: sendPlanning, isLoading: isSendPlanningLoading } = useSendPlanningMutation({ onSettled: () => refetch() });
    const { mutate: sendPlanningForActivity, isLoading: isSendActivityLoading } = useSendPlanningForActivityMutation({ onSettled: () => refetch() });
    const { mutate: getBackPlanning, isLoading: isGetBackPlanningLoading } = useGetBackPlanningMutation({ onSettled: () => refetch() });
    const { mutate: getBackPlanningForActivity, isLoading: isGetBackActivityLoading } = useGetBackPlanningForActivityMutation({
        onSettled: () => refetch(),
    });
    const { mutate: mutateActivityTruckManager, isLoading: isMutateTruckManagerLoading } = useActivityTruckManagerMutation({
        onSettled: () => refetch(),
    });

    const setSpecificHour = () => {
        const date = new Date(currentDate);
        date.setHours(7, 0);
        return date.toISOString();
    };

    const sortedActivities = useMemo(() => {
        return (activities || []).sort((a, b) => {
            if (
                formatName(a.siteId.conducId?.firstName, a.siteId.conducId?.lastName) <
                formatName(b.siteId.conducId?.firstName, b.siteId.conducId?.lastName)
            )
                return -1;
            if (
                formatName(a.siteId.conducId?.firstName, a.siteId.conducId?.lastName) >
                formatName(b.siteId.conducId?.firstName, b.siteId.conducId?.lastName)
            )
                return 1;

            const siteA = a.siteId.name.toLowerCase();
            const siteB = b.siteId.name.toLowerCase();
            if (siteA < siteB) return -1;
            if (siteA > siteB) return 1;

            if (a.isTruckManager) return -1;
            if (b.isTruckManager) return 1;

            if (a.reportId.userId.profileId.permsLevel < b.reportId.userId.profileId.permsLevel) return -1;
            if (a.reportId.userId.profileId.permsLevel > b.reportId.userId.profileId.permsLevel) return 1;

            return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
        });
    }, [activities]);

    const columnDefs = useMemo(
        () =>
            [
                {
                    cellStyle: (params) => {
                        const rowIndex = params.node.rowIndex;
                        if (rowIndex === null) return null;
                        const api = params.api;

                        const prevRowNode = api.getDisplayedRowAtIndex(rowIndex - 1);
                        const nextRowNode = api.getDisplayedRowAtIndex(rowIndex + 1);

                        const prevId = prevRowNode?.data?.siteId._id;
                        const nextId = nextRowNode?.data?.siteId._id;
                        const currentId = params.data?.siteId._id;

                        const marginTop = prevId !== currentId ? '5px' : '0';
                        const marginBottom = nextId !== currentId ? '5px' : '0';

                        const height = `calc(100% - ${parseInt(marginTop) + parseInt(marginBottom)}px)`;

                        return {
                            padding: 0,
                            marginLeft: 5,
                            backgroundColor: params.data?.siteId.conducId?.color || 'transparent',
                            marginTop,
                            marginBottom,
                            height,
                            borderBottomLeftRadius: nextId !== currentId ? 10 : 0,
                            borderBottomRightRadius: nextId !== currentId ? 10 : 0,
                            borderTopLeftRadius: prevId !== currentId ? 10 : 0,
                            borderTopRightRadius: prevId !== currentId ? 10 : 0,
                            width: 20,
                        };
                    },
                    colId: 'color',
                    resizable: false,
                    filter: false,
                    sortable: false,
                    minWidth: 30,
                    initialWidth: 30,
                    initialPinned: 'left',
                    valueGetter: (params) => formatName(params.data?.siteId.conducId?.firstName, params.data?.siteId.conducId?.lastName),
                    cellRenderer: () => '',
                    enableRowGroup: false,
                    suppressHeaderMenuButton: true,
                },
                {
                    headerName: t('site'),
                    field: 'siteId.name',
                    initialWidth: 300,
                    initialPinned: 'left',
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        const rowIndex = params.node.rowIndex;
                        if (rowIndex === null) return null;
                        const api = params.api;

                        const prevRowNode = api.getDisplayedRowAtIndex(rowIndex - 1);
                        const nextRowNode = api.getDisplayedRowAtIndex(rowIndex + 1);

                        const prevId = prevRowNode?.data?.siteId._id;
                        const nextId = nextRowNode?.data?.siteId._id;
                        return params.data ? (
                            <SiteCell
                                activity={params.data}
                                currentDate={currentDate}
                                refetch={refetch}
                                disabled={!!user && user?.profileId.permsLevel > permissionLevelEnum.PLANNING}
                                remainingMaterial
                                previousSiteId={prevId}
                                nextSiteId={nextId}
                            />
                        ) : (
                            params.value
                        );
                    },
                    colId: 'site',
                },
                {
                    headerName: capitalizeFirstLetter(t('person')),
                    colId: 'name',
                    initialPinned: 'left',
                    initialWidth: 250,
                    field: 'reportId.userId',
                    valueFormatter: (params) => (params.value ? formatName(params.value.firstName, params.value.lastName) : ''),
                    cellEditor: 'agRichSelectCellEditor',
                    getQuickFilterText: (params) => (params.value ? formatName(params.value.firstName, params.value.lastName) : ''),
                    cellEditorParams: {
                        values: (params) =>
                            [...dataUsers].sort((a, b) => formatName(a.firstName, a.lastName).localeCompare(formatName(b.firstName, b.lastName))),
                        formatValue: (user) => (user ? formatName(user.firstName, user.lastName) : ''),
                        valueListMaxHeight: 500,
                    } as IRichCellEditorParams,

                    keyCreator: (params) => params.value?._id,
                    onCellValueChanged: async (params) => {
                        const newUser = params.newValue;
                        if (!newUser?._id || newUser._id === params.oldValue?._id) return;

                        const response = await createReportIfEmpty({
                            userId: newUser._id,
                            date: params.data.startTime,
                            state: reportStateEnum.PLANNING,
                        });
                        updateActivity({ id: params.data._id, body: { reportId: response.data._id } });
                    },
                    editable: (params) => !!params.data && checkEditableRow(params.data),
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        const user = params.value;
                        const activity = params.data;
                        if (!activity) return params.value;

                        const updateActivityIsSpecialHidden = (isSpecialHidden: boolean) => {
                            updateActivity({ id: activity?._id || '', body: { isSpecialHidden: isSpecialHidden } });
                        };

                        const updateActivityIsHighlight = (isHighlight: boolean) => {
                            updateActivity({ id: activity?._id || '', body: { isHighlight: isHighlight } });
                        };

                        const formatUserName = (u: any) => formatName(u.firstName, u.lastName);

                        const allRows: DataItem[] = [];

                        params.api.forEachNode((node) => {
                            if (node.data) allRows.push(node.data);
                        });

                        const countDuplicates = allRows.filter(
                            (a) => a?.reportId?.userId?._id === user?._id && !a?.reportId?.userId?.isSpecial
                        ).length;

                        const isDuplicate = countDuplicates > 1;

                        return (
                            <div
                                className="fx-r fx-items-c fx-justify-sb w-100 border-radius-10px h-25px pad-l-5px pad-r-5px"
                                style={{
                                    opacity: checkEditableRow(activity) ? undefined : 0.6,
                                    backgroundColor:
                                        (activity?.reportId.userId.isSpecial ? !activity.isSpecialHidden : activity?.isHighlight) &&
                                        activity?.activityState === activityStateEnum.PLANNING
                                            ? activity.reportId.userId.isSpecial
                                                ? muiTheme.palette.info.main
                                                : muiTheme.palette.warning.main
                                            : activity?.reportId.reportType === reportTypeEnum.ABSENT
                                              ? muiTheme.palette.error.main
                                              : undefined,
                                }}
                            >
                                <span>{user ? formatUserName(user) : ''}</span>
                                <span className="fx-r fx-items-c">
                                    <div title={capitalizeFirstLetter(t('absent'))}>
                                        {activity?.reportId.reportType === reportTypeEnum.ABSENT && '❗'}
                                    </div>
                                    <div title={capitalizeFirstLetter(t('truckManager'))}>{activity?.isTruckManager && '🎗'}</div>
                                    {isDuplicate ? (
                                        <div className="bg-orange border-radius-10px text-black h-20px w-20px fx-r fx-items-c fx-justify-c">
                                            <div>{countDuplicates}</div>
                                        </div>
                                    ) : null}
                                    {checkEditableRow(activity) &&
                                        activity.reportId.userId.isSpecial &&
                                        activity.activityState === activityStateEnum.PLANNING && (
                                            <div style={{ paddingLeft: 5, paddingRight: 5 }} className="only-over">
                                                <CustomToggle
                                                    checked={activity.isSpecialHidden || false}
                                                    onChange={updateActivityIsSpecialHidden}
                                                    title={t('validate')}
                                                />
                                            </div>
                                        )}
                                    {checkEditableRow(activity) &&
                                        !activity.reportId.userId.isSpecial &&
                                        activity.activityState === activityStateEnum.PLANNING && (
                                            <div style={{ paddingLeft: 5, paddingRight: 5 }} className="only-over">
                                                <CustomToggle
                                                    checked={activity.isHighlight || false}
                                                    onChange={updateActivityIsHighlight}
                                                    title={t('validate')}
                                                />
                                            </div>
                                        )}
                                </span>
                            </div>
                        );
                    },
                    valueSetter: (params) => {
                        if (params.newValue) {
                            params.data.reportId.userId = params.newValue;
                            return true;
                        } else {
                            return false;
                        }
                    },
                },
                {
                    headerName: capitalizeFirstLetter(t('start')),
                    colId: 'start',
                    initialWidth: 100,
                    valueGetter: (params) => !!params.data && formatTime(new Date(params.data?.startTime)),
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        if (!params.data) return params.value;
                        return <StartTimeCell activity={params.data} refetch={refetch} disabled={!checkEditableRow(params.data)} />;
                    },
                },
                {
                    headerName: capitalizeFirstLetter(t('machines')),
                    colId: 'machines',
                    initialWidth: 300,
                    cellStyle: { overflow: 'visible' },
                    cellClass: 'ag-cell-remove-of',
                    valueGetter: (params) => params.data?.dailyMachines.map((m) => m.machineId.designation),
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                        const allRows: DataItem[] = [];

                        params.api.forEachNode((node) => {
                            if (node.data) allRows.push(node.data);
                        });

                        return (
                            <MachinesCell
                                activity={params.data}
                                activityIndex={params.node.rowIndex}
                                activities={allRows}
                                disabled={!checkEditableRow(params.data) || params.data.activityTypeId?.type === activityTypeEnum.TRUCK}
                                refetch={refetch}
                                createTransfer={false}
                            />
                        );
                    },
                    filter: 'agTextColumnFilter',
                    enableRowGroup: false,
                    enableValue: false,
                    sortable: false,
                },
                {
                    headerName: capitalizeFirstLetter(t('tools')),
                    colId: 'tools',
                    initialWidth: 300,
                    cellClass: 'ag-cell-remove-of',
                    valueGetter: (params) => params.data?.dailyTools.map((t) => t.toolId.designation),
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                        const allRows: DataItem[] = [];

                        params.api.forEachNode((node) => {
                            if (node.data) allRows.push(node.data);
                        });
                        return (
                            <ToolsCell
                                activity={params.data}
                                activityIndex={params.node.rowIndex}
                                activities={allRows}
                                disabled={!checkEditableRow(params.data) || params.data.activityTypeId?.type === activityTypeEnum.TRUCK}
                                refetch={refetch}
                                createTransfer={false}
                            />
                        );
                    },
                    filter: 'agTextColumnFilter',
                    enableRowGroup: false,
                    enableValue: false,
                    sortable: false,
                },
                {
                    headerName: capitalizeFirstLetter(t('rentals')),
                    colId: 'rentals',
                    initialWidth: 300,
                    cellClass: 'ag-cell-remove-of',
                    valueGetter: (params) => params.data?.dailyRentals.map((r) => `${r.designation} ${r.rentalTypeId.designation}`.trim()),
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                        const allRows: DataItem[] = [];

                        params.api.forEachNode((node) => {
                            if (node.data) allRows.push(node.data);
                        });
                        return (
                            <RentalsCell
                                activity={params.data}
                                activityIndex={params.node.rowIndex}
                                activities={allRows}
                                disabled={!checkEditableRow(params.data) || params.data.activityTypeId?.type === activityTypeEnum.TRUCK}
                                refetch={refetch}
                                createTransfer={false}
                            />
                        );
                    },
                    filter: 'agTextColumnFilter',
                    enableRowGroup: false,
                    enableValue: false,
                    sortable: false,
                },
                ...Object.values(truckGroupEnum).map(
                    (group): ColDef<DataItem> => ({
                        headerName: t(getTruckGroupLabel(group)),
                        initialWidth: 280,
                        cellClass: 'ag-cell-remove-of',
                        valueGetter: (params) =>
                            params.data?.dailyTrucksPlanned.map((tp) =>
                                `${tp.trucksAmount} ${tp.truckCompanyId?.designation} ${tp.ridesAmount || ''} ${getTruckModeLabel(tp.mode)} ${tp.truckId?.designation}`.trim()
                            ),
                        cellRenderer: (params: ICellRendererParams<DataItem>) => {
                            if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                            const allRows: DataItem[] = [];

                            params.api.forEachNode((node) => {
                                if (node.data) allRows.push(node.data);
                            });
                            return (
                                <TrucksPlannedCell
                                    activity={params.data}
                                    activityIndex={params.node.rowIndex}
                                    disabled={
                                        !checkEditableRow(params.data) ||
                                        (params.data.activityTypeId?.type !== activityTypeEnum.TRUCK && !params.data.isTruckManager)
                                    }
                                    group={group}
                                    refetch={refetch}
                                    activities={allRows}
                                    currentDate={currentDate}
                                />
                            );
                        },
                        colId: 'trucks' + group.toString(),
                        filter: 'agTextColumnFilter',
                        enableRowGroup: false,
                        enableValue: false,
                        sortable: false,
                    })
                ),
                {
                    headerName: t('observations'),
                    field: 'observationsPlanning',
                    minWidth: 300,
                    initialFlex: 1,
                    editable: (params) => !!params.data && checkEditableRow(params.data),
                    colId: 'observations',

                    valueFormatter: (params) => params.value || '',

                    valueSetter: (params) => {
                        const newValue = params.newValue?.trim() || '';
                        const oldValue = params.data.observationsPlanning?.trim() || '';
                        if (newValue !== oldValue) {
                            updateActivity({ id: params.data._id, body: { observationsPlanning: newValue } });
                            params.data.observationsPlanning = newValue;
                        }
                        return true;
                    },
                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    cellStyle: { whiteSpace: 'pre-wrap' },
                    filter: 'agTextColumnFilter',
                    enableRowGroup: false,
                    enableValue: false,
                    sortable: false,
                },
                {
                    headerName: capitalizeFirstLetter(t('tempWorkers')),
                    colId: 'tempworkers',
                    cellClass: 'ag-cell-remove-of',
                    valueGetter: (params) =>
                        params.data?.dailyTempWorkers.map((tw) =>
                            `${tw.tempWorkerTypeId.designation} ${tw.durationHours}h ${tw.tempWorkerName}`.trim()
                        ),
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                        return (
                            <TempWorkersCell
                                activity={params.data}
                                activityIndex={params.node.rowIndex}
                                activities={[]}
                                disabled={!checkEditableRow(params.data) || params.data.activityTypeId?.type === activityTypeEnum.TRUCK}
                                refetch={() => refetch()}
                            />
                        );
                    },
                    filter: 'agTextColumnFilter',
                    enableRowGroup: false,
                    enableValue: false,
                    sortable: false,
                },
                {
                    headerName: capitalizeFirstLetter(t('subContractors')),
                    colId: 'subcontractors',
                    cellClass: 'ag-cell-remove-of',
                    valueGetter: (params) => params.data?.dailySubContractors.map((sc) => `${sc.subContractorTypeId.designation}`.trim()),
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                        return (
                            <SubContractorsCell
                                activity={params.data}
                                activityIndex={params.node.rowIndex}
                                activities={[]}
                                disabled={!checkEditableRow(params.data) || params.data.activityTypeId?.type === activityTypeEnum.TRUCK}
                                refetch={() => refetch()}
                            />
                        );
                    },
                    filter: 'agTextColumnFilter',
                    enableRowGroup: false,
                    enableValue: false,
                    sortable: false,
                },
                {
                    headerName: t('truckManager'),
                    field: 'isTruckManager',
                    initialWidth: 100,
                    colId: 'truckmanager',
                    editable: (params) => {
                        if (!params.data) return false;
                        return checkEditableRow(params.data) && params.data.activityTypeId?.type !== activityTypeEnum.TRUCK;
                    },
                    cellRendererSelector: (params: ICellRendererParams<DataItem>) => {
                        if (!params.data) return params.value;
                        if (params.data.activityTypeId?.type === activityTypeEnum.TRUCK) {
                            const truckType = dataTrucks?.find((value) => value.userId?._id === params.data?.reportId?.userId?._id)?.truckTypeId
                                ?.designation;

                            return {
                                component: () => (
                                    <TruckIcon
                                        type={(truckType || '') as 'Semie' | 'Ampliroll' | '8x4' | 'Porte-char' | ''}
                                        disabled={params.data && !checkEditableRow(params.data)}
                                    />
                                ),
                            };
                        } else {
                            return {
                                component: 'agCheckboxCellRenderer',
                            };
                        }
                    },
                    onCellValueChanged: (event) => {
                        if (event.colDef.field === 'isTruckManager') {
                            const newValue = event.newValue;
                            const activityId = event.data._id;
                            mutateActivityTruckManager({ activityId, isTruckManager: newValue });
                        }
                    },
                },
                {
                    headerName: capitalizeFirstLetter(t('activity')),
                    field: 'activityTypeId',
                    colId: 'activity',
                    cellStyle: (params) => (params.data ? { opacity: checkEditableRow(params.data) ? undefined : 0.6 } : null),
                    valueFormatter: (params) => (params.value ? params.value.designation : ''),
                    cellEditor: 'agRichSelectCellEditor',
                    cellEditorParams: {
                        values: [...dataActivitiesTypes].sort((a, b) => a.designation.localeCompare(b.designation)),
                        formatValue: (activity) => (activity ? activity.designation : ''),
                        valueListMaxHeight: 500,
                    } as IRichCellEditorParams,
                    keyCreator: (params) => params.value?._id,
                    onCellValueChanged: async (params) => {
                        const newActivity = params.newValue;
                        if (!newActivity?._id || newActivity._id === params.oldValue?._id) return;
                        updateActivity({ id: params.data._id, body: { activityTypeId: newActivity._id } });
                    },
                    editable: (params) => !!params.data && checkEditableRow(params.data),
                    valueSetter: (params) => {
                        if (params.newValue) {
                            params.data.activityTypeId = params.newValue;
                            return true;
                        } else {
                            return false;
                        }
                    },
                },
                {
                    headerName: capitalizeFirstLetter(t('status')),
                    valueGetter: (params) => capitalizeFirstLetter(t(getActivityStateLabel(params.data?.activityState))),
                    cellRenderer: (params: ICellRendererParams<DataItem>) => {
                        if (!params.data) return params.value;
                        return (
                            <StateCell
                                stateColor={getActivityStateColor(params.data?.activityState)}
                                text={t(getActivityStateLabel(params.data?.activityState))}
                            />
                        );
                    },
                    colId: 'status',
                },
                {
                    headerName: capitalizeFirstLetter(t('actions')),
                    initialWidth: 200,
                    colId: 'actions',
                    sortable: false,
                    filter: false,
                    enableRowGroup: false,
                    enableValue: false,
                    cellRenderer: (params: ICellRendererParams<DataItem>) => (
                        <div>
                            {!!user && user?.profileId.permsLevel <= permissionLevelEnum.PLANNING && params.data?._id && (
                                <IconLine>
                                    {params.data?.activityState === activityStateEnum.PLANNINGSENT &&
                                        params.data?.reportId.reportState !== reportStateEnum.REPORTFILLED && (
                                            <IconButton
                                                iconName="resetTv"
                                                onClick={() => getBackPlanningForActivity({ activityId: params.data?._id || '' })}
                                                title={capitalizeFirstLetter(t('takeBackActivity'))}
                                                loading={isGetBackActivityLoading}
                                            />
                                        )}
                                    {params.data?.activityState === activityStateEnum.PLANNING && (
                                        <IconButton
                                            iconName="verticalAlignTop"
                                            onClick={() => sendPlanningForActivity({ activityId: params.data?._id || '' })}
                                            title={capitalizeFirstLetter(t('sendActivity'))}
                                            loading={isSendActivityLoading}
                                        />
                                    )}
                                    {params.data?.activityState === activityStateEnum.PLANNING && (
                                        <IconButton
                                            iconName="deleteForever"
                                            onClick={() => deleteActivity({ id: params.data?._id || '' })}
                                            title={capitalizeFirstLetter(t('deleteActivity'))}
                                            color="error"
                                        />
                                    )}
                                    <IconButton
                                        iconName="contentCopy"
                                        onClick={() => {
                                            setSelectedActivityForDuplicate(params.data || null);
                                            duplicateActivityModal.setTrue();
                                        }}
                                        title={capitalizeFirstLetter(t('duplicateActivity'))}
                                    />
                                </IconLine>
                            )}
                        </div>
                    ),
                },
                ...((user?.profileId.permsLevel === permissionLevelEnum.ADMIN
                    ? [
                          {
                              headerName: capitalizeFirstLetter(t('_id')),
                              field: '_id',
                              colId: '_id',
                              initialWidth: 250,
                              cellClass: 'usage-col-main',
                              headerClass: 'usage-col-main',
                          },
                      ]
                    : []) as ColDef<DataItem>[]),
            ] as ColDef<DataItem>[],
        [dataActivitiesTypes, dataUsers, dataTrucks, currentDate]
    );

    return (
        <>
            <GenericAgGridTable
                rowData={sortedActivities}
                columnDefs={columnDefs}
                loading={isLoading}
                exportCsvFileName={`planning-${formatDate(currentDate)}`}
                agGridProps={{
                    getRowId: ({ data }) => data._id,
                    autoGroupColumnDef: {
                        pinned: 'left',
                    },
                    getContextMenuItems: (params) => {
                        if (
                            [
                                'machines',
                                'tools',
                                'rentals',
                                ...Object.values(truckGroupEnum).map((group) => 'trucks' + group.toString()),
                                'tempworkers',
                                'subcontractors',
                            ].includes(params.column?.getColId() ?? '')
                        ) {
                            return [];
                        }
                        return params.defaultItems ?? [];
                    },
                    getRowStyle: (params) => {
                        const rowIndex = params.node.rowIndex;
                        if (rowIndex === null) return undefined;

                        const api = params.api;
                        const nextRowNode = api.getDisplayedRowAtIndex(rowIndex + 1);
                        const currentSiteId = params.data?.siteId._id;
                        const nextSiteId = nextRowNode?.data?.siteId._id;

                        if (nextSiteId && currentSiteId !== nextSiteId) {
                            return { borderBottom: '2px solid' };
                        }

                        return undefined;
                    },
                    rowSelection:
                        user && user?.profileId.permsLevel <= permissionLevelEnum.PLANNING
                            ? {
                                  mode: 'multiRow',
                                  hideDisabledCheckboxes: true,
                              }
                            : undefined,
                    onRowSelected: (event) => {
                        const selectedRows = event.api.getSelectedRows();
                        const selectedIds = selectedRows.map((row: any) => row._id);
                        setSelectedRows(selectedIds);
                    },
                    context: { sortedActivities },
                }}
                storageKey={localStorageKeys.TABLE.OPERATIONS.PLANNING.GLOBAL}
                headerRightComponent={
                    <>
                        {!!user && user?.profileId.permsLevel <= permissionLevelEnum.PLANNING && (
                            <>
                                <IconButton iconName="contentCopy" onClick={() => setShowDuplicateActivities(true)} disabled={!selectedRows.length} />
                                <Divider orientation="vertical" flexItem />
                                <IconButton
                                    iconName="addCircle"
                                    onClick={() => {
                                        createActivityModal.setTrue();
                                    }}
                                    title={capitalizeFirstLetter(t('addActivity'))}
                                />

                                <IconButton
                                    iconName="resetTv"
                                    onClick={() => getBackPlanning({ date: currentDate.toISOString() })}
                                    title={capitalizeFirstLetter(t('takeBackPlanning'))}
                                    loading={isGetBackPlanningLoading}
                                />
                                <IconButton
                                    iconName="verticalAlignTop"
                                    onClick={() => {
                                        sendPlanning({ date: currentDate.toISOString(), withNotification: true });
                                    }}
                                    title={capitalizeFirstLetter(t('sendPlanning'))}
                                    loading={isSendPlanningLoading}
                                />
                                <Divider orientation="vertical" flexItem />
                                <IconButton
                                    iconName="peopleAlt"
                                    onClick={() => remainingUsersModal.setTrue()}
                                    title={capitalizeFirstLetter(t('remainingUsers'))}
                                />
                                <IconButton
                                    iconName="precisionManufacturing"
                                    onClick={() => remainingMaterialModal.setTrue()}
                                    title={capitalizeFirstLetter(t('remainingMaterial'))}
                                />
                                <Divider orientation="vertical" flexItem />
                                <ToggleButtonGroup value={0} exclusive onChange={handleAlignment}>
                                    <ToggleButton value={0}>{capitalizeFirstLetter(t('planning'))}</ToggleButton>
                                    <ToggleButton value={1}>{capitalizeFirstLetter(t('ampliroll'))}</ToggleButton>
                                </ToggleButtonGroup>
                            </>
                        )}
                    </>
                }
            />
            {showDuplicateActivities && (
                <DuplicateActivitiesModal
                    activityIds={selectedRows}
                    handleClose={() => setShowDuplicateActivities(false)}
                    open={showDuplicateActivities}
                />
            )}
            {duplicateActivityModal.value && (
                <DuplicateActivityModal
                    open={duplicateActivityModal.value}
                    handleClose={duplicateActivityModal.setFalse}
                    activityId={selectedActivityForDuplicate?._id || ''}
                    name={formatName(selectedActivityForDuplicate?.reportId.userId.firstName, selectedActivityForDuplicate?.reportId.userId.lastName)}
                />
            )}
            {createActivityModal.value && (
                <CreateActivityModal
                    open={createActivityModal.value}
                    handleClose={createActivityModal.setFalse}
                    refetch={refetch}
                    initDate={setSpecificHour()}
                    currentDate={currentDate}
                    reportStateToShow={[reportStateEnum.NEEDS, reportStateEnum.PLANNING]}
                />
            )}
            {remainingUsersModal.value && (
                <RemainingPeopleModal
                    open={remainingUsersModal.value}
                    handleClose={remainingUsersModal.setFalse}
                    activities={activities || []}
                    initDate={setSpecificHour()}
                    refetch={refetch}
                    currentDate={currentDate}
                    users={dataUsers}
                />
            )}
            {remainingMaterialModal.value && (
                <RemainingMaterialModal
                    open={remainingMaterialModal.value}
                    handleClose={remainingMaterialModal.setFalse}
                    activities={activities || []}
                    initDate={setSpecificHour()}
                    refetch={refetch}
                    currentDate={currentDate}
                />
            )}
        </>
    );
};

export default PlanningGlobalTable;
